/**
 * Dashboard Registry Tab Controller
 * ---------------------------------
 * Surfaces the host's gift-registry overview as a tab inside the response
 * dashboard — an easier place to view it, next to Responses / Messaging /
 * Seating. The canonical overview still lives in the registry builder
 * (/gift-registry?id=…); this tab mirrors it in the dashboard's native style
 * and deep-links out to the full builder for editing.
 *
 * Registries are OWNER-scoped (not tied to a specific event/form), so the tab
 * follows the FORM'S OWNER — not whoever is signed in. Normally those are the
 * same person. When they differ:
 *   - ADMIN viewing a host's dashboard → the HOST's registry, via
 *     /my-registries?owner=<uid> (the server only honours ?owner= for admins).
 *   - anyone else (co-owner / planner) → tab stands down; someone else's gift
 *     money is not theirs to see, and showing their OWN registry here would be
 *     both confusing and wrong.
 * If the current form is linked to a registry (formData.linkedRegistryId) that
 * one is preselected; otherwise the most recent is shown, with a selector when
 * there are several.
 *
 * Money: guests always CONTRIBUTE in USD, so gift/item totals are USD. But the
 * couple SETTLES in their connected account's own currency — an Israeli account
 * pays out ILS, a German one EUR (~40% of registry accounts are non-US). So the
 * payout figures carry their own `currency` and must be rendered with it. Do not
 * reintroduce a hardcoded '$' on anything payout-shaped.
 */
class DashboardRegistry {
    // Long lists scroll inside their panel rather than growing the tab. Real
    // registries are far bigger than the demo (47 gifts / 74 contributions is a
    // live one), and uncapped they push everything else off the screen.
    static PANEL_SCROLL = 'max-height:340px;overflow-y:auto';

    static TRANSLATIONS = {
        en: {
            summary: 'Summary', totalRaised: 'Total in gifts', contributions: 'Contributions',
            gifts: 'Gifts', fullyFunded: 'Fully funded', giftsAndFunds: 'Gifts & funds',
            recentContributions: 'Recent contributions', openFull: 'Open full registry',
            exportCsv: 'Export CSV', date: 'Date', guest: 'Guest', toward: 'Toward', amount: 'You received', message: 'Message',
            anonymous: 'Anonymous', cashGift: 'Cash gift', openFund: 'open fund', funded: 'Funded',
            noContribs: 'No contributions yet', noGifts: 'No gifts yet — add gifts in the registry builder.',
            live: 'Live', draft: 'Draft', loading: 'Loading…',
            loadError: "Couldn't load your registry", registry: 'Registry',
            nudgeTitle: 'Add a gift registry', nudgeDesc: 'Collect cash gifts or list the gifts you want — your guests can contribute straight from your invitation.',
            nudgeCta: 'Create a registry', nudgeFree: 'Free', ownerOnlyTitle: 'The gift registry is the host\'s', ownerOnlyDesc: 'Only the event\'s owner can set up a registry and receive its gifts. When they add one, it shows here and on the invitation.',
            paidToBank: 'Paid to your bank', payoutsTitle: 'Payouts', viewInStripe: 'View in Stripe',
            onTheWay: 'On the way', arrived: 'Arrived', payoutPending: 'Pending', payoutFailed: 'Failed',
            noPayouts: 'No payouts yet — your first gift will reach your bank about {days} days after it arrives.',
            noPayoutsSameDay: 'No payouts yet — your first gift will be sent on to your bank as soon as it clears.',
            payoutsAuto: 'Payouts are automatic. Each gift is sent to your bank about {days} days after a guest gives it, and takes a day or two more to appear — there is nothing to withdraw.',
            payoutsAutoSameDay: 'Payouts are automatic. Each gift is sent on to your bank as soon as it clears — there is nothing to withdraw.',
            payoutsAllOut: 'Everything has reached your bank. Nothing is being held.',
            heldArriving: '{amount} from recent gifts is on its way — each gift is sent to your bank about {days} days after a guest gives it, so it won’t show in Stripe until then.',
            finishSetup: 'Finish payout setup', setupToGetPaid: 'Connect a bank account so your gifts can reach you.',
            payoutsUnavailable: "Couldn't load payouts right now.",
            feesCovered: 'guest covered the fee', giftWord: 'gift', feeWord: 'fee',
            payoutsBlocked: 'Payouts are paused — Stripe needs something from you before your money can move.',
            bankEnding: 'Bank ending',
            fxNote: 'Guests give in USD — your bank receives {cur} after conversion, so this won’t match the gift totals exactly.'
        },
        es: {
            summary: 'Resumen', totalRaised: 'Total en regalos', contributions: 'Contribuciones',
            gifts: 'Regalos', fullyFunded: 'Completados', giftsAndFunds: 'Regalos y fondos',
            recentContributions: 'Contribuciones recientes', openFull: 'Abrir lista completa',
            exportCsv: 'Exportar CSV', date: 'Fecha', guest: 'Invitado', toward: 'Para', amount: 'Has recibido', message: 'Mensaje',
            anonymous: 'Anónimo', cashGift: 'Regalo en efectivo', openFund: 'fondo abierto', funded: 'Completado',
            noContribs: 'Aún no hay contribuciones', noGifts: 'Aún no hay regalos — añádelos en el editor de la lista.',
            live: 'Publicada', draft: 'Borrador', loading: 'Cargando…',
            loadError: 'No se pudo cargar tu lista', registry: 'Lista de regalos',
            nudgeTitle: 'Añade una lista de regalos', nudgeDesc: 'Recibe regalos en efectivo o publica los regalos que quieres: tus invitados pueden contribuir desde la invitación.',
            nudgeCta: 'Crear una lista', nudgeFree: 'Gratis', ownerOnlyTitle: 'La lista de regalos es del anfitrión', ownerOnlyDesc: 'Solo el dueño del evento puede crear una lista y recibir sus regalos. Cuando la añada, aparecerá aquí y en la invitación.',
            paidToBank: 'Ingresado en tu banco', payoutsTitle: 'Pagos', viewInStripe: 'Ver en Stripe',
            onTheWay: 'En camino', arrived: 'Ingresado', payoutPending: 'Pendiente', payoutFailed: 'Fallido',
            noPayouts: 'Aún no hay pagos: tu primer regalo llegará a tu banco unos {days} días después de recibirlo.',
            noPayoutsSameDay: 'Aún no hay pagos: tu primer regalo se enviará a tu banco en cuanto se confirme.',
            payoutsAuto: 'Los pagos son automáticos. Cada regalo se envía a tu banco unos {days} días después de que un invitado lo hace, y tarda un día o dos más en aparecer — no tienes que retirar nada.',
            payoutsAutoSameDay: 'Los pagos son automáticos. Cada regalo se envía a tu banco en cuanto se confirma — no tienes que retirar nada.',
            payoutsAllOut: 'Todo ha llegado a tu banco. No queda nada retenido.',
            heldArriving: '{amount} de regalos recientes está en camino: cada regalo se envía a tu banco unos {days} días después de que un invitado lo hace, así que hasta entonces no aparecerá en Stripe.',
            finishSetup: 'Completar configuración de pagos', setupToGetPaid: 'Conecta una cuenta bancaria para recibir tus regalos.',
            payoutsUnavailable: 'No se pudieron cargar los pagos ahora mismo.',
            feesCovered: 'el invitado cubrió la comisión', giftWord: 'regalo', feeWord: 'comisión',
            payoutsBlocked: 'Los pagos están en pausa: Stripe necesita algo de ti antes de poder mover tu dinero.',
            bankEnding: 'Cuenta terminada en',
            fxNote: 'Los invitados dan en USD — tu banco recibe {cur} tras la conversión, así que no coincidirá exactamente con los totales de regalos.'
        },
        fr: {
            summary: 'Résumé', totalRaised: 'Total des cadeaux', contributions: 'Contributions',
            gifts: 'Cadeaux', fullyFunded: 'Financés', giftsAndFunds: 'Cadeaux et cagnottes',
            recentContributions: 'Contributions récentes', openFull: 'Ouvrir la liste complète',
            exportCsv: 'Exporter CSV', date: 'Date', guest: 'Invité', toward: 'Pour', amount: 'Vous avez reçu', message: 'Message',
            anonymous: 'Anonyme', cashGift: 'Cadeau en argent', openFund: 'cagnotte ouverte', funded: 'Financé',
            noContribs: 'Aucune contribution pour l’instant', noGifts: 'Aucun cadeau — ajoutez-en dans l’éditeur de liste.',
            live: 'En ligne', draft: 'Brouillon', loading: 'Chargement…',
            loadError: 'Impossible de charger votre liste', registry: 'Liste de cadeaux',
            nudgeTitle: 'Ajoutez une liste de cadeaux', nudgeDesc: 'Recevez des cadeaux en argent ou listez les cadeaux souhaités — vos invités contribuent directement depuis l’invitation.',
            nudgeCta: 'Créer une liste', nudgeFree: 'Gratuit', ownerOnlyTitle: 'La liste de cadeaux appartient à l’hôte', ownerOnlyDesc: 'Seul le propriétaire de l’événement peut créer une liste et recevoir ses cadeaux. Quand il en ajoutera une, elle apparaîtra ici et sur l’invitation.',
            paidToBank: 'Versé sur votre compte', payoutsTitle: 'Versements', viewInStripe: 'Voir dans Stripe',
            onTheWay: 'En route', arrived: 'Reçu', payoutPending: 'En attente', payoutFailed: 'Échec',
            noPayouts: 'Aucun versement pour l’instant — votre premier cadeau arrivera sur votre compte environ {days} jours après.',
            noPayoutsSameDay: 'Aucun versement pour l’instant — votre premier cadeau sera envoyé sur votre compte dès qu’il est validé.',
            payoutsAuto: 'Les versements sont automatiques. Chaque cadeau part vers votre compte environ {days} jours après, et met un jour ou deux de plus à apparaître — rien à retirer.',
            payoutsAutoSameDay: 'Les versements sont automatiques. Chaque cadeau est envoyé sur votre compte dès qu’il est validé — rien à retirer.',
            payoutsAllOut: 'Tout est arrivé sur votre compte. Rien n’est retenu.',
            heldArriving: '{amount} de cadeaux récents est en route — chaque cadeau part vers votre compte environ {days} jours après, il n’apparaîtra donc pas dans Stripe avant.',
            finishSetup: 'Terminer la configuration', setupToGetPaid: 'Connectez un compte bancaire pour recevoir vos cadeaux.',
            payoutsUnavailable: 'Impossible de charger les versements pour le moment.',
            feesCovered: 'l’invité a payé les frais', giftWord: 'cadeau', feeWord: 'frais',
            payoutsBlocked: 'Les versements sont suspendus — Stripe a besoin d’une information avant de transférer votre argent.',
            bankEnding: 'Compte finissant par',
            fxNote: 'Les invités donnent en USD — votre compte reçoit des {cur} après conversion, ce montant ne correspond donc pas exactement aux totaux des cadeaux.'
        },
        de: {
            summary: 'Übersicht', totalRaised: 'Gesamt an Geschenken', contributions: 'Beiträge',
            gifts: 'Geschenke', fullyFunded: 'Finanziert', giftsAndFunds: 'Geschenke & Kassen',
            recentContributions: 'Letzte Beiträge', openFull: 'Vollständige Liste öffnen',
            exportCsv: 'CSV exportieren', date: 'Datum', guest: 'Gast', toward: 'Für', amount: 'Ihr habt erhalten', message: 'Nachricht',
            anonymous: 'Anonym', cashGift: 'Geldgeschenk', openFund: 'offene Kasse', funded: 'Finanziert',
            noContribs: 'Noch keine Beiträge', noGifts: 'Noch keine Geschenke — im Listen-Editor hinzufügen.',
            live: 'Live', draft: 'Entwurf', loading: 'Wird geladen…',
            loadError: 'Eure Liste konnte nicht geladen werden', registry: 'Geschenkeliste',
            nudgeTitle: 'Geschenkeliste hinzufügen', nudgeDesc: 'Sammelt Geldgeschenke oder listet eure Wunschgeschenke — eure Gäste können direkt über die Einladung beitragen.',
            nudgeCta: 'Liste erstellen', nudgeFree: 'Kostenlos', ownerOnlyTitle: 'Die Geschenkeliste gehört dem Gastgeber', ownerOnlyDesc: 'Nur der Inhaber des Events kann eine Liste anlegen und die Geschenke erhalten. Sobald er eine hinzufügt, erscheint sie hier und in der Einladung.',
            paidToBank: 'Auf euer Konto überwiesen', payoutsTitle: 'Auszahlungen', viewInStripe: 'In Stripe ansehen',
            onTheWay: 'Unterwegs', arrived: 'Angekommen', payoutPending: 'Ausstehend', payoutFailed: 'Fehlgeschlagen',
            noPayouts: 'Noch keine Auszahlungen — euer erstes Geschenk erreicht euer Konto etwa {days} Tage nach Eingang.',
            noPayoutsSameDay: 'Noch keine Auszahlungen — euer erstes Geschenk geht an euer Konto, sobald es bestätigt ist.',
            payoutsAuto: 'Auszahlungen laufen automatisch. Jedes Geschenk geht etwa {days} Tage nachdem ein Gast es gegeben hat an euer Konto und braucht ein bis zwei Tage mehr, bis es sichtbar ist — ihr müsst nichts abheben.',
            payoutsAutoSameDay: 'Auszahlungen laufen automatisch. Jedes Geschenk geht an euer Konto, sobald es bestätigt ist — ihr müsst nichts abheben.',
            payoutsAllOut: 'Alles ist auf eurem Konto angekommen. Es wird nichts zurückgehalten.',
            heldArriving: '{amount} aus aktuellen Geschenken ist unterwegs — jedes Geschenk geht etwa {days} Tage nach Eingang an euer Konto und ist bis dahin nicht in Stripe sichtbar.',
            finishSetup: 'Auszahlungen einrichten', setupToGetPaid: 'Verbindet ein Bankkonto, damit eure Geschenke ankommen.',
            payoutsUnavailable: 'Auszahlungen konnten gerade nicht geladen werden.',
            feesCovered: 'Gast hat die Gebühr übernommen', giftWord: 'Geschenk', feeWord: 'Gebühr',
            payoutsBlocked: 'Auszahlungen pausiert — Stripe benötigt noch etwas von euch, bevor das Geld fließen kann.',
            bankEnding: 'Konto endend auf',
            fxNote: 'Gäste schenken in USD — euer Konto erhält {cur} nach der Umrechnung, daher stimmt dieser Betrag nicht exakt mit den Geschenksummen überein.'
        },
        pt: {
            summary: 'Resumo', totalRaised: 'Total em presentes', contributions: 'Contribuições',
            gifts: 'Presentes', fullyFunded: 'Concluídos', giftsAndFunds: 'Presentes e fundos',
            recentContributions: 'Contribuições recentes', openFull: 'Abrir lista completa',
            exportCsv: 'Exportar CSV', date: 'Data', guest: 'Convidado', toward: 'Para', amount: 'Recebeu', message: 'Mensagem',
            anonymous: 'Anónimo', cashGift: 'Presente em dinheiro', openFund: 'fundo aberto', funded: 'Concluído',
            noContribs: 'Ainda não há contribuições', noGifts: 'Ainda não há presentes — adicione-os no editor da lista.',
            live: 'Publicada', draft: 'Rascunho', loading: 'A carregar…',
            loadError: 'Não foi possível carregar a sua lista', registry: 'Lista de presentes',
            nudgeTitle: 'Adicione uma lista de presentes', nudgeDesc: 'Receba presentes em dinheiro ou publique os presentes que deseja — os convidados contribuem a partir do convite.',
            nudgeCta: 'Criar uma lista', nudgeFree: 'Grátis', ownerOnlyTitle: 'A lista de presentes é do anfitrião', ownerOnlyDesc: 'Só o dono do evento pode criar uma lista e receber os presentes. Quando ele adicionar uma, ela aparece aqui e no convite.',
            paidToBank: 'Transferido para o seu banco', payoutsTitle: 'Transferências', viewInStripe: 'Ver no Stripe',
            onTheWay: 'A caminho', arrived: 'Recebido', payoutPending: 'Pendente', payoutFailed: 'Falhou',
            noPayouts: 'Ainda não há transferências — o seu primeiro presente chega ao banco cerca de {days} dias depois.',
            noPayoutsSameDay: 'Ainda não há transferências — o seu primeiro presente segue para o banco assim que for confirmado.',
            payoutsAuto: 'As transferências são automáticas. Cada presente segue para o seu banco cerca de {days} dias depois de o convidado o oferecer e demora mais um ou dois dias a aparecer — não precisa de levantar nada.',
            payoutsAutoSameDay: 'As transferências são automáticas. Cada presente segue para o seu banco assim que for confirmado — não precisa de levantar nada.',
            payoutsAllOut: 'Tudo chegou ao seu banco. Não há nada retido.',
            heldArriving: '{amount} de presentes recentes está a caminho — cada presente segue para o seu banco cerca de {days} dias depois de ser oferecido, por isso só aparece no Stripe nessa altura.',
            finishSetup: 'Concluir configuração', setupToGetPaid: 'Ligue uma conta bancária para receber os seus presentes.',
            payoutsUnavailable: 'Não foi possível carregar as transferências neste momento.',
            feesCovered: 'o convidado cobriu a taxa', giftWord: 'presente', feeWord: 'taxa',
            payoutsBlocked: 'As transferências estão suspensas — o Stripe precisa de algo antes de mover o seu dinheiro.',
            bankEnding: 'Conta terminada em',
            fxNote: 'Os convidados dão em USD — o seu banco recebe {cur} após a conversão, por isso não corresponderá exatamente aos totais dos presentes.'
        },
        he: {
            summary: 'סיכום', totalRaised: 'סה״כ במתנות', contributions: 'תרומות',
            gifts: 'מתנות', fullyFunded: 'מומנו', giftsAndFunds: 'מתנות וקרנות',
            recentContributions: 'תרומות אחרונות', openFull: 'פתחו את הרשימה המלאה',
            exportCsv: 'ייצוא CSV', date: 'תאריך', guest: 'אורח', toward: 'עבור', amount: 'קיבלתם', message: 'ברכה',
            anonymous: 'אנונימי', cashGift: 'מתנת מזומן', openFund: 'קרן פתוחה', funded: 'מומן',
            noContribs: 'עדיין אין תרומות', noGifts: 'עדיין אין מתנות — הוסיפו אותן בעורך הרשימה.',
            live: 'פעילה', draft: 'טיוטה', loading: 'טוען…',
            loadError: 'לא ניתן לטעון את הרשימה שלכם', registry: 'רשימת מתנות',
            nudgeTitle: 'הוסיפו רשימת מתנות', nudgeDesc: 'אספו מתנות כסף או פרסמו את המתנות שאתם רוצים — האורחים יכולים לתרום ישירות מההזמנה.',
            nudgeCta: 'צרו רשימה', nudgeFree: 'חינם', ownerOnlyTitle: 'רשימת המתנות שייכת לבעלי האירוע', ownerOnlyDesc: 'רק בעלי האירוע יכולים ליצור רשימה ולקבל את המתנות. כשיוסיפו אחת, היא תופיע כאן ובהזמנה.',
            paidToBank: 'הועבר לחשבון שלכם', payoutsTitle: 'העברות', viewInStripe: 'צפייה ב-Stripe',
            onTheWay: 'בדרך', arrived: 'הגיע', payoutPending: 'ממתין', payoutFailed: 'נכשל',
            noPayouts: 'עדיין אין העברות — המתנה הראשונה תגיע לחשבון כ-{days} ימים אחרי שתתקבל.',
            noPayoutsSameDay: 'עדיין אין העברות — המתנה הראשונה תישלח לחשבון שלכם ברגע שתאושר.',
            payoutsAuto: 'ההעברות אוטומטיות. כל מתנה נשלחת לחשבון שלכם כ-{days} ימים אחרי שאורח נותן אותה, ולוקח עוד יום-יומיים עד שהיא מופיעה — אין צורך למשוך כלום.',
            payoutsAutoSameDay: 'ההעברות אוטומטיות. כל מתנה נשלחת לחשבון שלכם ברגע שהיא מאושרת — אין צורך למשוך כלום.',
            payoutsAllOut: 'הכל הגיע לחשבון שלכם. לא מוחזק אצלנו שום סכום.',
            heldArriving: '{amount} ממתנות אחרונות בדרך אליכם — כל מתנה נשלחת לחשבון שלכם כ-{days} ימים אחרי שאורח נותן אותה, ולכן היא לא תופיע ב-Stripe עד אז.',
            finishSetup: 'השלימו הגדרת תשלומים', setupToGetPaid: 'חברו חשבון בנק כדי שהמתנות יגיעו אליכם.',
            payoutsUnavailable: 'לא ניתן לטעון את ההעברות כרגע.',
            feesCovered: 'האורח כיסה את העמלה', giftWord: 'מתנה', feeWord: 'עמלה',
            payoutsBlocked: 'ההעברות מושהות — Stripe צריך מכם משהו לפני שהכסף יוכל לזוז.',
            bankEnding: 'חשבון המסתיים ב-',
            fxNote: 'האורחים נותנים בדולרים — הבנק שלכם מקבל {cur} אחרי המרה, ולכן הסכום לא יתאים במדויק לסכומי המתנות.'
        },
        ar: {
            summary: 'ملخص', totalRaised: 'إجمالي الهدايا', contributions: 'المساهمات',
            gifts: 'الهدايا', fullyFunded: 'مكتملة', giftsAndFunds: 'الهدايا والصناديق',
            recentContributions: 'أحدث المساهمات', openFull: 'فتح القائمة الكاملة',
            exportCsv: 'تصدير CSV', date: 'التاريخ', guest: 'ضيف', toward: 'من أجل', amount: 'استلمتَ', message: 'رسالة',
            anonymous: 'مجهول', cashGift: 'هدية نقدية', openFund: 'صندوق مفتوح', funded: 'مكتمل',
            noContribs: 'لا مساهمات بعد', noGifts: 'لا هدايا بعد — أضِفها من محرّر القائمة.',
            live: 'مباشرة', draft: 'مسودة', loading: 'جارٍ التحميل…',
            loadError: 'تعذّر تحميل قائمتك', registry: 'قائمة الهدايا',
            nudgeTitle: 'أضِف قائمة هدايا', nudgeDesc: 'اجمع الهدايا النقدية أو اعرض الهدايا التي تريدها — يمكن لضيوفك المساهمة مباشرة من الدعوة.',
            nudgeCta: 'إنشاء قائمة', nudgeFree: 'مجاناً', ownerOnlyTitle: 'قائمة الهدايا تخص صاحب المناسبة', ownerOnlyDesc: 'صاحب المناسبة وحده يمكنه إنشاء قائمة واستلام هداياها. عندما يضيف واحدة ستظهر هنا وفي الدعوة.',
            paidToBank: 'حُوِّل إلى حسابك', payoutsTitle: 'التحويلات', viewInStripe: 'عرض في Stripe',
            onTheWay: 'في الطريق', arrived: 'وصل', payoutPending: 'قيد الانتظار', payoutFailed: 'فشل',
            noPayouts: 'لا تحويلات بعد — ستصل أول هدية إلى حسابك بعد {days} أيام تقريباً من استلامها.',
            noPayoutsSameDay: 'لا تحويلات بعد — ستُرسل أول هدية إلى حسابك بمجرد تأكيدها.',
            payoutsAuto: 'التحويلات تلقائية. تُرسل كل هدية إلى حسابك بعد {days} أيام تقريباً من تقديم الضيف لها، وتستغرق يوماً أو يومين إضافيين حتى تظهر — لا حاجة لسحب أي شيء.',
            payoutsAutoSameDay: 'التحويلات تلقائية. تُرسل كل هدية إلى حسابك بمجرد تأكيدها — لا حاجة لسحب أي شيء.',
            payoutsAllOut: 'وصل كل شيء إلى حسابك. لا يوجد أي مبلغ محتجز.',
            heldArriving: '{amount} من الهدايا الأخيرة في طريقه إليك — تُرسل كل هدية إلى حسابك بعد {days} أيام تقريباً من تقديم الضيف لها، ولذلك لا تظهر في Stripe قبل ذلك.',
            finishSetup: 'أكمل إعداد التحويلات', setupToGetPaid: 'اربط حساباً بنكياً لتصلك هداياك.',
            payoutsUnavailable: 'تعذّر تحميل التحويلات الآن.',
            feesCovered: 'الضيف تكفّل بالرسوم', giftWord: 'هدية', feeWord: 'رسوم',
            payoutsBlocked: 'التحويلات متوقفة — يحتاج Stripe إلى معلومات منك قبل تحويل أموالك.',
            bankEnding: 'حساب منتهٍ بـ',
            fxNote: 'يقدّم الضيوف بالدولار — ويستلم بنكك {cur} بعد التحويل، لذا لن يتطابق هذا المبلغ تماماً مع إجمالي الهدايا.'
        }
    };

    constructor() {
        this.lang = window.DASHBOARD_LANGUAGE || document.documentElement.lang || 'en';
        this.registries = [];   // list from /my-registries (id + title + status)
        this.selectedId = null;
        this.renderedId = null; // last id whose detail is painted (avoids refetch)
        this.contribs = [];
        // Set to the form owner's uid when an ADMIN is viewing someone else's
        // dashboard; null means "self" (the normal case).
        this.foreignOwner = null;
    }

    t(key) {
        const s = DashboardRegistry.TRANSLATIONS[this.lang] || DashboardRegistry.TRANSLATIONS.en;
        return s[key] || DashboardRegistry.TRANSLATIONS.en[key] || key;
    }

    isDemo() { return !!window.isDemoMode; }

    money(cents) {
        const n = (Number(cents) || 0) / 100;
        try {
            return n.toLocaleString(this.lang, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
        } catch (e) {
            return '$' + Math.round(n).toLocaleString();
        }
    }

    // Exact cents, in an explicit currency. Two things this must respect:
    //  - money() rounds to whole dollars: fine for gift totals, but it would
    //    render 8386.80 as "$8,387" and the point of the payout figures is that
    //    they match a bank statement line-for-line.
    //  - PAYOUTS ARE NOT ALWAYS USD. Guests pay USD, but the couple settles in
    //    their account's currency — an Israeli account pays out ILS, a German
    //    one EUR. Callers pass the settlement currency; never assume dollars.
    moneyExact(cents, currency) {
        const n = (Number(cents) || 0) / 100;
        const cur = String(currency || 'USD').toUpperCase();
        try {
            return n.toLocaleString(this.lang, {
                style: 'currency', currency: cur,
                minimumFractionDigits: 2, maximumFractionDigits: 2
            });
        } catch (e) {
            return n.toFixed(2) + ' ' + cur;
        }
    }

    escapeHtml(text) {
        const d = document.createElement('div');
        d.textContent = (text == null ? '' : String(text));
        return d.innerHTML;
    }

    async token() {
        const u = (window.firebase && firebase.auth) ? firebase.auth().currentUser : null;
        return u ? u.getIdToken() : null;
    }

    async api(path) {
        const token = await this.token();
        if (!token) throw new Error('Not authenticated');
        const res = await fetch('/api/registry' + path, { headers: { Authorization: 'Bearer ' + token } });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error((data && data.error) || 'Request failed');
        return data;
    }

    async apiPost(path, body) {
        const token = await this.token();
        if (!token) throw new Error('Not authenticated');
        const res = await fetch('/api/registry' + path, {
            method: 'POST',
            headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
            body: JSON.stringify(body || {})
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error((data && data.error) || 'Request failed');
        return data;
    }

    // Called from admin.js after form/responses load. The tab is ALWAYS visible
    // (the registry is free — hosts without one get a nudge to create one).
    // Cheap: only lists registries here; the full detail is fetched lazily when
    // the tab is opened.
    async updateTabVisibility(formData) {
        const nav = document.getElementById('registry-tab-nav');
        if (!nav) return;
        nav.style.display = '';
        this.foreignOwner = null;
        try {
            if (this.isDemo()) {
                this.registries = [{ id: 'demo', title: this._demoRegistry().title, status: 'active' }];
            } else {
                // This tab belongs to the FORM'S OWNER, not to whoever is signed in.
                // An admin opening a host's dashboard must see the HOST's registry
                // (?owner= — admin-gated server-side), never their own. Anyone else
                // looking at a form they don't own (co-owner / planner) has no claim
                // to either registry — it's someone else's financial data — so the
                // tab stands down rather than showing them their own by accident.
                const me = (window.firebase && firebase.auth && firebase.auth().currentUser)
                    ? firebase.auth().currentUser.uid : null;
                const ownerUid = formData && formData.owner;
                // Task #810: the tab never disappears. A co-host / planner who
                // is not the owner still sees it, with a card that says the
                // registry is the owner's - never a "create" button, which
                // would open one in THEIR account and pay the gifts to them.
                this.notOwner = false;
                if (me && ownerUid && me !== ownerUid) {
                    if (!(await this.isAdminViewer())) {
                        this.notOwner = true;
                        this.registries = [];
                        this.selectedId = null;
                        if (document.getElementById('registry-tab')?.classList.contains('active')) this.onShown(true);
                        return;
                    }
                    this.foreignOwner = ownerUid;
                }
                const data = await this.api('/my-registries' +
                    (this.foreignOwner ? ('?owner=' + encodeURIComponent(this.foreignOwner)) : ''));
                this.registries = (data.registries || []).filter(Boolean);
            }
        } catch (e) {
            // Not signed in / request failed → fall through to the nudge.
            console.warn('[registry tab] could not list registries:', e.message);
            this.registries = [];
        }
        // Task #825: only a registry that belongs to THIS event. The list above
        // is every registry the owner has, and when the event had none linked
        // this took the first of them - so a planner's event with no registry
        // showed another client's, gifts, payouts and all. A registry belongs
        // to an event by its own formId (set when made from the event, or
        // attached on My Events), or by the event's linkedRegistryId when that
        // registry has not since been attached somewhere else.
        const formId = this.formIdOf(formData);
        this.allRegistries = this.registries;
        this.registries = this.registries.filter((r) => (formId && r.formId === formId)
            || (formData && formData.linkedRegistryId === r.id && (!r.formId || r.formId === formId)));
        // Registries tied to no event at all can be attached here with one
        // click - offered, never assumed.
        this.unattached = this.allRegistries.filter((r) => !r.formId && !r._coOwned
            && !this.registries.some((x) => x.id === r.id));
        // Esther's question on #825 - should a host who is NOT a planner still
        // get their registry here automatically? Yes, where it cannot be anyone
        // else's: 19 of the 23 live registries (2026-09-30) were never tied to
        // an event, and those hosts have always found theirs on this tab. So a
        // host with exactly ONE registry, attached to no event, still sees it.
        // A planner never does - one registry per client event is the norm
        // there, and a wrong guess shows one client another's gifts.
        const own = this.allRegistries.filter((r) => !r._coOwned);
        if (!this.registries.length && own.length === 1 && !own[0].formId
            && !(await this.ownerIsPlanner())) {
            this.registries = own;
            this.unattached = [];
        }
        if (this.registries.length) {
            if (!this.selectedId || !this.registries.some(r => r.id === this.selectedId)) this.selectedId = this.registries[0].id;
        } else {
            this.selectedId = null;
            // Admin viewing a host who has no registry: the "create one" nudge
            // would create the ADMIN's registry, so onShown shows the
            // owner-only card instead (task #810: the tab itself stays).
        }
        // If the tab is already open, refresh it for the (possibly new) selection.
        if (document.getElementById('registry-tab')?.classList.contains('active')) this.onShown(true);
    }

    /**
     * Task #825: is the event's owner an Event Planner? Only asked when the
     * viewer IS the owner (a non-owner never gets here; an admin viewing a
     * host gets the strict rule), so the signed-in user's own planner status -
     * planner-subscription.js, loaded on every dashboard - is the owner's.
     * Unknown counts as yes, which is the strict rule.
     */
    async ownerIsPlanner() {
        if (this.foreignOwner) return true;
        const PS = window.PlannerSubscription;
        if (!PS) return true;
        try {
            if (PS.isActive && PS.isActive() === true) return true;
            const st = PS.getStatus ? await PS.getStatus() : null;
            return !st || st.isActive === true;
        } catch (e) {
            return true;
        }
    }

    formIdOf(formData) {
        let fromUrl = null;
        try { fromUrl = new URLSearchParams(window.location.search).get('formId'); } catch (_) { /* no URL */ }
        return (formData && (formData.formId || formData.id)) || window.currentFormId || fromUrl || null;
    }

    /** Task #825: the words for attaching an existing registry, in the dashboard's 7 languages. */
    linkStrings() {
        const S = {
            en: { or: 'Or use a registry you already have:', use: 'Use for this event', failed: 'Could not attach the registry. Please try again.' },
            es: { or: 'O usa una lista que ya tienes:', use: 'Usar para este evento', failed: 'No se pudo vincular la lista. Inténtalo de nuevo.' },
            fr: { or: 'Ou utilisez une liste existante :', use: 'Utiliser pour cet événement', failed: 'Impossible de lier la liste. Réessayez.' },
            de: { or: 'Oder eine vorhandene Liste verwenden:', use: 'Für dieses Event verwenden', failed: 'Die Liste konnte nicht verknüpft werden. Bitte erneut versuchen.' },
            pt: { or: 'Ou use uma lista que você já tem:', use: 'Usar neste evento', failed: 'Não foi possível vincular a lista. Tente novamente.' },
            he: { or: 'או השתמשו ברשימה שכבר יש לכם:', use: 'לשימוש באירוע הזה', failed: 'לא ניתן לקשר את הרשימה. נסו שוב.' },
            ar: { or: 'أو استخدم قائمة لديك بالفعل:', use: 'استخدامها لهذه المناسبة', failed: 'تعذر ربط القائمة. حاول مرة أخرى.' }
        };
        return S[String(this.lang || 'en').slice(0, 2)] || S.en;
    }

    async attachExisting(registryId, btn) {
        const formId = this.formIdOf(window.currentFormData || null);
        if (!registryId || !formId) return;
        if (btn) btn.disabled = true;
        try {
            const token = await this.token();
            const res = await fetch('/api/registry/' + encodeURIComponent(registryId), {
                method: 'PUT',
                headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
                body: JSON.stringify({ formId })
            });
            if (!res.ok) throw new Error('HTTP ' + res.status);
            (this.allRegistries || []).forEach((r) => { if (r.id === registryId) r.formId = formId; });
            this.registries = (this.allRegistries || []).filter((r) => r.formId === formId);
            this.unattached = (this.unattached || []).filter((r) => r.id !== registryId);
            this.selectedId = registryId;
            await this.load(registryId);
        } catch (e) {
            console.warn('[registry tab] attach failed:', e.message);
            if (btn) btn.disabled = false;
            window.languageHelpers?.showToast?.(this.linkStrings().failed, 'error');
        }
    }

    // Firebase custom claim; the server re-checks it on every ?owner= request —
    // this only decides what we bother asking for.
    async isAdminViewer() {
        const u = (window.firebase && firebase.auth) ? firebase.auth().currentUser : null;
        if (!u) return false;
        try {
            const r = await u.getIdTokenResult();
            return !!(r && r.claims && r.claims.admin === true);
        } catch (e) {
            return false;
        }
    }

    async onShown(force) {
        if (!this.selectedId) {
            // Never nudge someone who is not the owner to create their own.
            if (this.foreignOwner || this.notOwner) { this.renderOwnerOnly(); return; }
            this.renderNudge();
            return;
        }
        if (!force && this.renderedId === this.selectedId) return;
        await this.load(this.selectedId);
    }

    // No registry yet — the feature is free, so nudge rather than hide the tab.
    renderNudge() {
        const body = document.getElementById('registry-tab-body');
        if (!body) return;
        this.renderedId = null;
        body.innerHTML =
            '<div style="display:flex;align-items:center;justify-content:center;padding:80px 20px 40px;">' +
                '<div class="text-center" style="max-width:400px;">' +
                    '<div style="width:64px;height:64px;margin:0 auto 16px;border-radius:50%;background:linear-gradient(135deg,#BC9B5D,#e0c88f);display:flex;align-items:center;justify-content:center;">' +
                        '<i class="bi bi-gift text-white" style="font-size:1.8rem;"></i>' +
                    '</div>' +
                    '<h5 class="fw-bold" style="color:#3a3150;">' + this.escapeHtml(this.t('nudgeTitle')) + '</h5>' +
                    '<p class="text-muted small mb-3">' + this.escapeHtml(this.t('nudgeDesc')) + '</p>' +
                    '<a href="/gift-registry?new=1' + (this.formIdOf(window.currentFormData || null) ? '&formId=' + encodeURIComponent(this.formIdOf(window.currentFormData || null)) : '') + '" class="btn btn-primary px-4" style="background:linear-gradient(135deg,#BC9B5D,#cfae72);border:none;">' +
                        '<i class="bi bi-plus-circle me-1"></i>' + this.escapeHtml(this.t('nudgeCta')) + '</a>' +
                    '<div class="mt-2"><span class="badge bg-success-subtle text-success-emphasis border border-success-subtle">' + this.escapeHtml(this.t('nudgeFree')) + '</span></div>' +
                    this.unattachedHtml() +
                '</div>' +
            '</div>';
        body.querySelectorAll('[data-attach-registry]').forEach((btn) => {
            btn.addEventListener('click', () => this.attachExisting(btn.getAttribute('data-attach-registry'), btn));
        });
    }

    // Task #825: the owner's registries that belong to no event, offered to
    // attach here (at most three; the rest are on My Events).
    unattachedHtml() {
        const list = (this.unattached || []).slice(0, 3);
        if (!list.length || this.isDemo()) return '';
        const L = this.linkStrings();
        return '<div class="mt-4 pt-3 border-top text-start">' +
            '<div class="small text-muted mb-2">' + this.escapeHtml(L.or) + '</div>' +
            list.map((r) => '<div class="d-flex align-items-center justify-content-between gap-2 border rounded-3 px-3 py-2 mb-2 bg-white">' +
                '<span class="text-truncate"><i class="bi bi-gift me-1 text-secondary"></i>' + this.escapeHtml(r.title || this.t('registry')) + '</span>' +
                '<button type="button" class="btn btn-sm btn-outline-secondary flex-shrink-0" data-attach-registry="' + this.escapeHtml(r.id) + '">' +
                this.escapeHtml(L.use) + '</button></div>').join('') +
            '</div>';
    }

    // Task #810: what a non-owner sees on an event with no registry.
    renderOwnerOnly() {
        const body = document.getElementById('registry-tab-body');
        if (!body) return;
        this.renderedId = null;
        body.innerHTML =
            '<div style="display:flex;align-items:center;justify-content:center;padding:80px 20px 40px;">' +
                '<div class="text-center" style="max-width:420px;">' +
                    '<div style="width:64px;height:64px;margin:0 auto 16px;border-radius:50%;background:#f3ede1;display:flex;align-items:center;justify-content:center;">' +
                        '<i class="bi bi-gift" style="font-size:1.8rem;color:#BC9B5D;"></i>' +
                    '</div>' +
                    '<h5 class="fw-bold" style="color:#3a3150;">' + this.escapeHtml(this.t('ownerOnlyTitle')) + '</h5>' +
                    '<p class="text-muted small mb-0">' + this.escapeHtml(this.t('ownerOnlyDesc')) + '</p>' +
                '</div>' +
            '</div>';
    }

    async load(id) {
        const body = document.getElementById('registry-tab-body');
        if (!body) return;
        body.innerHTML = this._centered('<div class="spinner-border text-secondary" role="status"></div>' +
            '<div class="mt-2 text-muted">' + this.t('loading') + '</div>');
        try {
            let reg, contribs, payout;
            if (this.isDemo() || id === 'demo') {
                reg = this._demoRegistry();
                contribs = this._demoContribs();
                payout = this._demoPayouts();
            } else {
                const [d1, d2, d3] = await Promise.all([
                    this.api('/' + id),
                    this.api('/' + id + '/contributions'),
                    // Payouts round-trip to Stripe. If that's slow or down the
                    // rest of the tab must still paint — degrade to a notice.
                    this.api('/' + id + '/connect/payouts').catch(e => {
                        console.warn('[registry tab] payouts unavailable:', e.message);
                        return { _failed: true };
                    })
                ]);
                reg = d1.registry;
                contribs = (d2.contributions || []);
                payout = d3;
            }
            this.contribs = contribs;
            this.renderedId = id;
            this.render(reg, contribs, payout);
        } catch (e) {
            console.error('[registry tab] load failed:', e);
            body.innerHTML = this._centered('<i class="bi bi-exclamation-triangle text-warning fs-3"></i>' +
                '<div class="mt-2 text-muted">' + this.escapeHtml(this.t('loadError')) + '</div>');
        }
    }

    render(reg, contribs, payout) {
        const body = document.getElementById('registry-tab-body');
        if (!body) return;
        const items = (reg.items || []);
        const isLive = reg.status === 'active';
        const giftCount = items.length + (reg.cashGiftEnabled ? 1 : 0);
        const funded = items.filter(i => i.isFunded).length;
        const cardStyle = 'box-shadow:0 4px 14px rgba(0,0,0,0.07);border-radius:12px;';

        // Selector (only when the owner has more than one registry)
        let selector = '';
        if (!this.isDemo() && this.registries.length > 1) {
            selector = '<select class="form-select form-select-sm w-auto" id="registry-select" aria-label="' + this.escapeHtml(this.t('registry')) + '">' +
                this.registries.map(r => '<option value="' + this.escapeHtml(r.id) + '"' + (r.id === this.selectedId ? ' selected' : '') + '>' +
                    this.escapeHtml(r.title || this.t('registry')) + '</option>').join('') + '</select>';
        }

        const openHref = this.isDemo() ? '/gift-registry' : ('/gift-registry?id=' + encodeURIComponent(reg.id));
        const exportBtn = this.isDemo() ? '' :
            '<button class="btn btn-sm btn-outline-secondary" id="registry-export-btn"><i class="bi bi-download me-1"></i>' + this.t('exportCsv') + '</button>';

        // Gifts & funds list
        let giftsHtml = '';
        if (reg.cashGiftEnabled) {
            // The cash fund has no target, so it carries no per-item amountRaised.
            // What it took = everything raised that didn't go to an item.
            const itemsRaised = items.reduce((s, i) => s + (Number(i.amountRaised) || 0), 0);
            const fundRaised = Math.max(0, (Number(reg.totalRaised) || 0) - itemsRaised);
            giftsHtml += '<div class="mb-3"><div class="d-flex justify-content-between align-items-center">' +
                '<span class="fw-medium text-truncate"><i class="bi bi-piggy-bank me-1 text-secondary"></i>' +
                this.escapeHtml(reg.cashGiftTitle || this.t('cashGift')) + '</span>' +
                '<span class="small text-muted ms-2">' + this.money(fundRaised) + ' · ' + this.t('openFund') + '</span></div></div>';
        }
        items.forEach(it => {
            const price = Number(it.price) || 0;
            const raised = Number(it.amountRaised) || 0;
            const pct = price > 0 ? Math.min(100, Math.round((raised / price) * 100)) : 0;
            giftsHtml += '<div class="mb-3"><div class="d-flex justify-content-between align-items-center mb-1">' +
                '<span class="fw-medium text-truncate" style="max-width:60%">' + this.escapeHtml(it.name) + '</span>' +
                '<span class="small text-muted ms-2">' + this.money(raised) + ' / ' + this.money(price) +
                (it.isFunded ? ' <span class="badge bg-success ms-1">' + this.t('funded') + '</span>' : '') + '</span></div>' +
                '<div class="progress" style="height:6px;"><div class="progress-bar ' + (it.isFunded ? 'bg-success' : '') +
                '" role="progressbar" style="width:' + pct + '%"></div></div></div>';
        });
        if (!reg.cashGiftEnabled && !items.length) {
            giftsHtml = '<div class="text-center text-muted py-3 small">' + this.escapeHtml(this.t('noGifts')) + '</div>';
        }

        // Recent contributions table
        let rows;
        if (!contribs.length) {
            rows = '<tr><td colspan="5" class="text-center text-muted py-4">' + this.t('noContribs') + '</td></tr>';
        } else {
            rows = contribs.map(c => {
                const name = c.isAnonymous ? this.t('anonymous') : (c.contributorName || this.t('guest'));
                const toward = c.itemId ? this._itemName(items, c.itemId) : (reg.cashGiftTitle || this.t('cashGift'));
                const gift = Number(c.amount) || 0;
                const charged = Number(c.chargeAmount) || 0;
                const net = (c.netAmount == null) ? gift : (Number(c.netAmount) || 0);
                // Whether the guest covered the fee is what decides if the gift
                // arrives whole — a "$100 gift" means two different things
                // depending on it, so say which one this was on every row.
                const note = charged > gift
                    ? this.t('feesCovered')
                    : (this.moneyExact(gift) + ' ' + this.t('giftWord') + ' · ' +
                       this.moneyExact(Math.max(0, gift - net)) + ' ' + this.t('feeWord'));
                // The message is the part hosts actually want to read — it's why
                // guests wrote it. Guest-authored, so escape it.
                const msg = (c.contributorMessage || '').trim();
                return '<tr><td class="text-muted small text-nowrap">' + this.escapeHtml(this._when(c.createdAt || c.completedAt)) + '</td>' +
                    '<td class="text-nowrap">' + this.escapeHtml(name) + '</td>' +
                    '<td class="text-muted text-nowrap">' + this.escapeHtml(toward) + '</td>' +
                    '<td class="small" style="min-width:180px">' + (msg
                        ? '<span class="fst-italic text-body-secondary">' + this.escapeHtml(msg) + '</span>'
                        : '<span class="text-muted">—</span>') + '</td>' +
                    '<td class="text-end text-nowrap"><div class="fw-medium">' + this.moneyExact(net) + '</div>' +
                        '<div class="text-muted" style="font-size:.7rem;line-height:1.25">' + this.escapeHtml(note) + '</div></td></tr>';
            }).join('');
        }

        body.innerHTML =
            '<div class="registry-tab-inner">' +
            '<div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">' +
                '<div class="d-flex align-items-center gap-2 flex-wrap">' +
                    '<h5 class="mb-0"><i class="bi bi-gift text-primary me-2"></i>' + this.escapeHtml(reg.title || this.t('registry')) + '</h5>' +
                    '<span class="badge ' + (isLive ? 'bg-success' : 'bg-secondary') + '">' + this.t(isLive ? 'live' : 'draft') + '</span>' +
                '</div>' +
                '<div class="d-flex align-items-center gap-2 flex-wrap">' + selector +
                    '<a href="' + openHref + '" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary"><i class="bi bi-box-arrow-up-right me-1"></i>' + this.t('openFull') + '</a>' +
                    exportBtn +
                '</div>' +
            '</div>' +
            // native summary stats bar (matches the Responses tab)
            '<div class="stats-bar"><div class="stats-bar-title"><i class="bi bi-graph-up"></i> ' + this.t('summary') + '</div>' +
                // Four stats, deliberately. "Paid to your bank" is NOT repeated
                // here: the payouts card beside Gifts shows it at 1.5rem with the
                // bank and status, and a 5th stat squeezes each to ~69px at
                // 375px — where an exact figure over $9,999.99 spills its box.
                // Four fit: at 375px each gets ~87px and a real five-figure
                // total ("$12,490") measures ~56px, so it has room.
                '<div class="stats-bar-items">' +
                    this._stat('primary', 'bi-cash-stack', this.money(reg.totalRaised), this.t('totalRaised')) +
                    this._stat('secondary', 'bi-people', (reg.totalContributions || contribs.length || 0), this.t('contributions')) +
                    this._stat('info', 'bi-gift', giftCount, this.t('gifts')) +
                    this._stat('success', 'bi-check-circle', funded, this.t('fullyFunded')) +
                '</div>' +
            '</div>' +
            // Layout: the money answer + gift progress share a row, then the
            // activity log gets the full width — guest messages need horizontal
            // room to read, and they are what hosts actually come here for.
            //
            // Both list panels scroll internally (PANEL_SCROLL). A real registry
            // is not the demo's 3 gifts / 6 contributions: Rena & Akiva have 47
            // gifts and 74 contributions, which uncapped render a ~6000px tab
            // that buries everything below it.
            '<div class="d-flex flex-column flex-lg-row gap-3 mt-3">' +
                '<div style="flex:1 1 42%;min-width:0">' + this._payoutsCard(payout, cardStyle) + '</div>' +
                '<div style="flex:1 1 58%;min-width:0"><div class="card analytics-card h-100" style="' + cardStyle + '"><div class="card-body">' +
                    '<h6 class="card-title mb-3">' + this.t('giftsAndFunds') +
                        (giftCount ? ' <span class="badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle ms-1">' + giftCount + '</span>' : '') +
                    '</h6>' +
                    '<div style="' + DashboardRegistry.PANEL_SCROLL + '">' + giftsHtml + '</div>' +
                '</div></div></div>' +
            '</div>' +
            '<div class="card analytics-card mt-3" style="' + cardStyle + '"><div class="card-body">' +
                '<h6 class="card-title mb-3">' + this.t('recentContributions') +
                    (contribs.length ? ' <span class="badge bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle ms-1">' + contribs.length + '</span>' : '') +
                '</h6>' +
                '<div class="table-responsive" style="' + DashboardRegistry.PANEL_SCROLL + '">' +
                    '<table class="table table-sm align-middle mb-0">' +
                        '<thead class="position-sticky top-0 bg-white" style="z-index:1"><tr>' +
                            '<th>' + this.t('date') + '</th><th>' + this.t('guest') + '</th><th>' + this.t('toward') + '</th>' +
                            '<th>' + this.t('message') + '</th>' +
                            '<th class="text-end">' + this.t('amount') + '</th>' +
                        '</tr></thead>' +
                        '<tbody>' + rows + '</tbody></table></div>' +
            '</div></div>' +
            '</div>';

        // Wire controls
        const sel = document.getElementById('registry-select');
        if (sel) sel.addEventListener('change', e => { this.selectedId = e.target.value; this.load(e.target.value); });
        const exp = document.getElementById('registry-export-btn');
        if (exp) exp.addEventListener('click', () => this.exportCsv(reg));
        const stripeBtn = document.getElementById('registry-stripe-btn');
        if (stripeBtn) stripeBtn.addEventListener('click', () => this.openStripeDashboard(stripeBtn));
    }

    /**
     * Stripe Express dashboard login links are single-use and short-lived, so
     * one is minted per click rather than baked into the page.
     */
    async openStripeDashboard(btn) {
        if (this.isDemo()) return;
        // The tab must be opened synchronously inside the click — popup
        // blockers reject window.open() once we've awaited the fetch. Can't
        // pass 'noopener' here (that makes open() return null and we'd lose the
        // handle), so detach the opener by hand instead.
        const win = window.open('about:blank', '_blank');
        if (win) win.opener = null;

        const original = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span>';
        try {
            const data = await this.apiPost('/' + this.selectedId + '/connect/login-link');
            if (win) win.location.href = data.url;
            else window.location.href = data.url;   // popup blocked → same tab
        } catch (e) {
            if (win) win.close();
            console.error('[registry tab] login link failed:', e);
            if (window.showToast) window.showToast(e.message || this.t('payoutsUnavailable'), 'error');
        } finally {
            btn.disabled = false;
            btn.innerHTML = original;
        }
    }

    _stat(kind, icon, value, label) {
        return '<div class="stat-inline stat-' + kind + '"><i class="bi ' + icon + '"></i>' +
            '<span class="stat-value">' + this.escapeHtml(value) + '</span>' +
            '<span class="stat-label">' + this.escapeHtml(label) + '</span></div>';
    }

    /**
     * "Did all our money reach our bank?" — answered without leaving the tab.
     *
     * Every figure here comes from Stripe payouts, which are the lines that
     * show up on the owner's bank statement. Deliberately NOT computed from
     * stored contribution.netAmount: that is what we intended to transfer at
     * checkout, and it can drift from what Stripe actually moved, which would
     * reintroduce the very mismatch this card exists to remove.
     */
    _payoutsCard(payout, cardStyle) {
        // h-100, not mt-3: this card now sits in the flex row beside Gifts &
        // funds rather than full-width above it.
        const wrap = (inner) =>
            '<div class="card analytics-card h-100" style="' + cardStyle + '"><div class="card-body">' + inner + '</div></div>';
        const title = '<h6 class="card-title mb-1"><i class="bi bi-bank me-1"></i>' +
            this.escapeHtml(this.t('payoutsTitle')) + '</h6>';

        // Stripe hiccup — say so plainly rather than implying zero. 'error' and
        // 'not_configured' come back as connected:false too, but they are NOT
        // evidence the host lacks a bank account: telling someone with $12k in
        // gifts to "connect a bank account" because Stripe blipped would be
        // alarming and false. Only a real absence earns the setup prompt.
        if (!payout || payout._failed || payout.status === 'error' || payout.status === 'not_configured') {
            return wrap(title + '<div class="text-muted small"><i class="bi bi-exclamation-circle me-1"></i>' +
                this.escapeHtml(this.t('payoutsUnavailable')) + '</div>');
        }

        // No bank connected yet: gifts have nowhere to land.
        if (!payout.connected) {
            return wrap(
                '<div class="d-flex flex-wrap align-items-center justify-content-between gap-2">' +
                    '<div>' + title +
                        '<div class="text-muted small">' + this.escapeHtml(this.t('setupToGetPaid')) + '</div>' +
                    '</div>' +
                    '<a href="/gift-registry?id=' + encodeURIComponent(this.selectedId || '') + '" class="btn btn-sm btn-primary">' +
                        this.escapeHtml(this.t('finishSetup')) + '</a>' +
                '</div>');
        }

        const payouts = payout.payouts || [];
        const held = (Number(payout.available) || 0) + (Number(payout.pending) || 0);
        // Settlement currency — ILS/EUR/… for non-US couples, not dollars.
        const cur = payout.currency || 'usd';

        const head =
            '<div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">' +
                '<div style="min-width:0">' + title +
                    '<div class="fw-bold" style="font-size:1.5rem;line-height:1.25;color:#3a3150">' +
                        this.moneyExact(payout.paidOut, cur) + '</div>' +
                    '<div class="text-muted small">' + this.escapeHtml(this.t('paidToBank')) +
                        (payout.bankLast4
                            ? ' · ' + this.escapeHtml(this.t('bankEnding')) + ' ' + this.escapeHtml(payout.bankLast4)
                            : '') +
                    '</div>' +
                '</div>' +
                (payout.canOpenDashboard
                    ? '<button type="button" class="btn btn-sm btn-outline-primary flex-shrink-0" id="registry-stripe-btn">' +
                        '<i class="bi bi-box-arrow-up-right me-1"></i>' + this.escapeHtml(this.t('viewInStripe')) + '</button>'
                    : '') +
            '</div>';

        let status = '';
        if (!payout.payoutsEnabled) {
            status = '<div class="alert alert-warning py-2 px-3 small mb-3">' +
                this.escapeHtml(this.t('payoutsBlocked')) + '</div>';
        } else if (payout.inTransit > 0) {
            status = '<div class="small mb-3"><span class="badge bg-info-subtle text-info-emphasis border border-info-subtle">' +
                this.escapeHtml(this.t('onTheWay')) + ' · ' + this.moneyExact(payout.inTransit, cur) + '</span></div>';
        } else if (held === 0 && !((Number(payout.heldCents) || 0) > 0) && payouts.length) {
            // "Nothing is being held" must also account for gifts still inside
            // OUR hold — those sit on the platform balance, invisible to every
            // Stripe-sourced figure here, so without the heldCents check this
            // line would flatly contradict the note rendered just below it.
            status = '<div class="small text-success mb-3"><i class="bi bi-check-circle me-1"></i>' +
                this.escapeHtml(this.t('payoutsAllOut')) + '</div>';
        }

        // Days before a gift reaches the bank.
        //
        // `holdDays` is OUR hold and is the only figure here that is actually
        // enforced: the money stays on the platform balance until the payout job
        // releases it. Prefer it whenever the server sends one.
        //
        // The Stripe schedule's delay_days is the fallback, and it is NOT a
        // timing promise — measured 2026-07-21 across every registry account, it
        // delays destination-charge money not at all (accounts set to 0, 2 and 7
        // all landed in the same 1-4.7 day band; the 7s were the fastest). Kept
        // only because it is the closest thing available when the held model is
        // off. The final fallback stays 7 rather than Stripe's 2 because it
        // fails in the safe direction: quoting 7 when money lands sooner is a
        // pleasant surprise, while quoting 2 when it takes longer reads as a
        // broken promise on the exact screen a host checks for missing money.
        const rawDelay = payout.payoutSchedule && payout.payoutSchedule.delay_days;
        const delayDays = Number.isFinite(payout.holdDays)
            ? payout.holdDays
            : (Number.isFinite(rawDelay) ? rawDelay : 7);

        // Gifts still inside the hold. Nothing above can show this — the money
        // has not been transferred, so it is on our balance, not the couple's
        // Stripe account. A host whose first gift just arrived would otherwise
        // open this card, see an empty account, and conclude the gift was lost.
        // Answering that is the whole reason this line exists.
        // heldCents is USD (no transfer yet), NOT the settlement currency `cur`.
        let heldNote = '';
        if ((Number(payout.heldCents) || 0) > 0) {
            heldNote = '<div class="alert alert-info py-2 px-3 small mb-3 d-flex align-items-start gap-2">' +
                '<i class="bi bi-clock-history mt-1 flex-shrink-0"></i><div>' +
                this.escapeHtml(this.t('heldArriving')
                    .replace('{amount}', this.moneyExact(payout.heldCents, payout.heldCurrency || 'usd'))
                    .replace('{days}', delayDays)) +
                '</div></div>';
        }
        const withDays = (key) => delayDays < 1
            ? this.t(key + 'SameDay')
            : this.t(key).replace('{days}', delayDays);

        let list;
        if (!payouts.length) {
            list = '<div class="text-muted small py-2">' + this.escapeHtml(withDays('noPayouts')) + '</div>';
        } else {
            list = '<div style="max-height:190px;overflow-y:auto"><table class="table table-sm align-middle mb-0">' +
                '<tbody>' + payouts.map(p =>
                    '<tr>' +
                        '<td class="text-muted small text-nowrap">' + this.escapeHtml(this._whenTs(p.arrivalDate || p.created)) + '</td>' +
                        '<td class="fw-medium text-nowrap">' + this.moneyExact(p.amount, p.currency || cur) + '</td>' +
                        '<td class="text-end">' + this._payoutBadge(p.status) + '</td>' +
                    '</tr>').join('') + '</tbody></table></div>';
        }

        // Guests always give USD, but a non-US couple's bank receives their own
        // currency after Stripe's FX. Without a word of explanation the panel
        // reads as a contradiction: an Israeli host with the display currency
        // set to ILS sees a converted gift total beside a REAL shekel payout
        // total, thousands apart, and asks where the rest went. They can never
        // tie out — gift totals convert at today's rate, payouts happened over
        // months at theirs — so explain rather than fake a match.
        const fxNote = String(cur).toLowerCase() !== 'usd'
            ? '<div class="text-muted mt-2" style="font-size:.72rem;line-height:1.45">' +
                  this.escapeHtml(this.t('fxNote').replace('{cur}', String(cur).toUpperCase())) + '</div>'
            : '';

        return wrap(head + status + heldNote + list +
            '<div class="text-muted mt-2" style="font-size:.72rem;line-height:1.45">' +
                this.escapeHtml(withDays('payoutsAuto')) + '</div>' + fxNote);
    }

    _payoutBadge(status) {
        const map = {
            paid: ['success', this.t('arrived')],
            in_transit: ['info', this.t('onTheWay')],
            pending: ['secondary', this.t('payoutPending')],
            failed: ['danger', this.t('payoutFailed')],
            canceled: ['danger', this.t('payoutFailed')]
        };
        const pair = map[status] || ['secondary', status];
        return '<span class="badge bg-' + pair[0] + '-subtle text-' + pair[0] + '-emphasis border border-' + pair[0] + '-subtle">' +
            this.escapeHtml(pair[1]) + '</span>';
    }

    // Stripe timestamps are unix SECONDS (_when handles Firestore shapes).
    _whenTs(sec) {
        if (!sec) return '';
        const d = new Date(sec * 1000);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString(this.lang, { month: 'short', day: 'numeric' });
    }

    _centered(inner) {
        return '<div class="text-center py-5">' + inner + '</div>';
    }

    _itemName(items, itemId) {
        const it = items.filter(x => x.id === itemId)[0];
        return it ? it.name : this.t('cashGift');
    }

    _when(ts) {
        if (!ts) return '';
        let d;
        if (typeof ts === 'object' && ts._seconds) d = new Date(ts._seconds * 1000);
        else if (typeof ts === 'object' && typeof ts.seconds === 'number') d = new Date(ts.seconds * 1000);
        else d = new Date(ts);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString(this.lang, { month: 'short', day: 'numeric' });
    }

    async exportCsv(reg) {
        try {
            const token = await this.token();
            if (!token) return;
            const res = await fetch('/api/registry/' + reg.id + '/export', { headers: { Authorization: 'Bearer ' + token } });
            if (!res.ok) throw new Error('Export failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = (reg.title || 'registry').replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '-contributions.csv';
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('[registry tab] export failed:', e);
            if (window.showToast) window.showToast(this.t('loadError'), 'error');
        }
    }

    // ---- Demo sample data (shown in the demo dashboard for exposure) ----
    // Sample data must ADD UP — the fund's raised amount is derived as
    // totalRaised − Σ item.amountRaised, so any drift shows as a wrong number
    // on a public marketing surface. Items 84400 + fund 25000 = 109400 total,
    // and the 6 contributions below sum to exactly that (d2: 20000 + 15000 = 35000).
    _demoRegistry() {
        return {
            id: 'demo', title: 'Sarah & Mike’s Wedding Registry', status: 'active',
            totalRaised: 109400, totalContributions: 6, cashGiftEnabled: true, cashGiftTitle: 'Honeymoon Fund',
            items: [
                { id: 'd1', name: 'Stand Mixer', price: 42900, amountRaised: 42900, isFunded: true },
                { id: 'd2', name: 'Espresso Machine', price: 69900, amountRaised: 35000, isFunded: false },
                { id: 'd3', name: 'Dinnerware Set', price: 18000, amountRaised: 6500, isFunded: false }
            ]
        };
    }
    // chargeAmount/netAmount are what make the fee story legible: where the
    // guest covered the fee (charge > gift) the couple receives the gift whole;
    // where they didn't (charge === gift) the fee comes out of it. Four covered
    // + two not, so the demo shows both. Fee = 6% + $0.30, matching production.
    // Nets: 15000 + 42900 + 18770 + 10000 + 15000 + 6080 = 107750, which is
    // exactly what _demoPayouts() pays out. Gifts still total 109400; the 1650
    // gap is the fee on the two uncovered gifts (1230 + 420).
    _demoContribs() {
        const day = 86400000;
        const base = new Date('2026-06-01T12:00:00Z').getTime();
        return [
            { contributorName: 'Rachel Green', amount: 15000, chargeAmount: 15930, netAmount: 15000, itemId: null, createdAt: new Date(base + 9 * day).toISOString() },
            { contributorName: 'Daniel Cohen', amount: 42900, chargeAmount: 45504, netAmount: 42900, itemId: 'd1', createdAt: new Date(base + 6 * day).toISOString() },
            { contributorName: 'Noa Baron', amount: 20000, chargeAmount: 20000, netAmount: 18770, itemId: 'd2', createdAt: new Date(base + 5 * day).toISOString() },
            { isAnonymous: true, amount: 10000, chargeAmount: 10630, netAmount: 10000, itemId: null, createdAt: new Date(base + 4 * day).toISOString() },
            { contributorName: 'Tal Shapiro', amount: 15000, chargeAmount: 15930, netAmount: 15000, itemId: 'd2', createdAt: new Date(base + 3 * day).toISOString() },
            { contributorName: 'Maya Levi', amount: 6500, chargeAmount: 6500, netAmount: 6080, itemId: 'd3', createdAt: new Date(base + 2 * day).toISOString() }
        ];
    }

    // Mirrors the real /connect/payouts shape. One payout per gift, arriving 2
    // days after it — the same cadence the card's footnote promises. Sums to
    // 107750 = the demo contributions' nets, so the card reconciles on screen.
    _demoPayouts() {
        const day = 86400;
        const base = Math.floor(new Date('2026-06-01T12:00:00Z').getTime() / 1000);
        // [days-after-base the money landed, amount]
        const payouts = [[11, 15000], [8, 42900], [7, 18770], [6, 10000], [5, 15000], [4, 6080]]
            .map(([d, amount], i) => ({
                id: 'po_demo' + i, amount, currency: 'usd', status: 'paid',
                created: base + (d - 1) * day, arrivalDate: base + d * day, failureMessage: null
            }));
        return {
            connected: true,
            paidOut: payouts.reduce((s, p) => s + p.amount, 0),
            inTransit: 0, failed: 0, available: 0, pending: 0,
            payouts,
            bankLast4: '4242',
            payoutsEnabled: true, detailsSubmitted: true,
            // No real Stripe account behind the demo — showing the button would
            // just be a dead control.
            canOpenDashboard: false,
            payoutSchedule: { interval: 'daily', delay_days: 2 }
        };
    }
}

// Global instance + tab wiring (mirrors dashboard-checkin.js / dashboard-seating.js)
window.dashboardRegistry = new DashboardRegistry();

document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('registry-tab')?.addEventListener('shown.bs.tab', () => {
        window.dashboardRegistry.onShown();
    });
});
// For real users the tab is revealed by admin.js after responses load. In the
// DEMO dashboard that path throws early (no real formId), AND window.isDemoMode
// is set by the page's own init which may run after our DOMContentLoaded — so
// re-check at window load (settled) and reveal with sample data then.
function _registryDemoReveal() {
    if (window.isDemoMode && window.dashboardRegistry) window.dashboardRegistry.updateTabVisibility(null);
}
document.addEventListener('DOMContentLoaded', _registryDemoReveal);
window.addEventListener('load', _registryDemoReveal);
